import React from 'react';
import { CheckCircle2, AlertTriangle, CameraOff, CircleSlash, Lock, Flag } from 'lucide-react';
import { SlotStatusBadge } from './SlotStatusBadge';
import { EvidenceSlotState, EvidenceSlotDispositionType } from '@services/evidenceSlot';

interface EvidenceSlotCardProps {
  slot: EvidenceSlotState;
  onOpenLightbox: (documentId: string) => void;
}

// Sprint 12, story-051 (U72). UX-A5 — icon/label per disposition type, invented for this design.
const DISPOSITION_ICON: Record<EvidenceSlotDispositionType, typeof CircleSlash> = {
  NOT_APPLICABLE: CircleSlash,
  ACCESS_DENIED: Lock,
  OTHER: Flag,
};

const DISPOSITION_LABEL: Record<EvidenceSlotDispositionType, string> = {
  NOT_APPLICABLE: 'Not Applicable',
  ACCESS_DENIED: 'Access Denied',
  OTHER: 'Other',
};

/**
 * Sprint 11, story-042 (U77). One fixed-slot cell; renders one of FILLED / EMPTY / FAILED per
 * slot, per docs/ux/story-042-design.md's wireframes. The empty-and-required cell is the exact
 * cell the business driver is about (50 of 68 rollout stores sat at zero photos and nothing
 * surfaced it) — it must never look like nothing rendered.
 */
export const EvidenceSlotCard: React.FC<EvidenceSlotCardProps> = ({ slot, onOpenLightbox }) => {
  const latestCapture = slot.captures[slot.captures.length - 1];

  // Sprint 12, story-051 (U72). A disposition is view-only on web (no camera) — clicking opens
  // the lightbox in its existing text-only mode, same pattern already used for a FAILED row with
  // no retrievable thumbnail. Solid border like a filled photo cell (it is resolved, not a
  // call-to-action), but blue/neutral, not green.
  if (slot.status === 'FILLED' && latestCapture?.dispositionType) {
    const DispositionIcon = DISPOSITION_ICON[latestCapture.dispositionType];
    return (
      <button
        type="button"
        data-testid={`evidence-slot-${slot.slotId}`}
        onClick={() => onOpenLightbox(latestCapture.documentId)}
        className="flex flex-col text-left gap-2 rounded-lg border border-blue-300 dark:border-blue-700 p-3 hover:shadow-md transition-shadow cursor-pointer">
        <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
          <DispositionIcon size={16} />
          <span className="text-sm font-medium">{slot.label}</span>
        </div>
        <div className="flex flex-col items-center justify-center h-28 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 gap-1 p-2">
          <DispositionIcon size={20} />
          <span className="text-sm font-medium">{DISPOSITION_LABEL[latestCapture.dispositionType]}</span>
        </div>
        <span className="text-xs text-frozenGrayShades6 dark:text-frozenGrayShades4">
          {new Date(latestCapture.capturedAt).toLocaleString()}
          {latestCapture.capturedByName ? ` by ${latestCapture.capturedByName}` : ''}
        </span>
        <div className="flex gap-1">
          <SlotStatusBadge variant={slot.required ? 'required' : 'optional'} />
          <SlotStatusBadge variant="dispositioned" />
        </div>
      </button>
    );
  }

  if (slot.status === 'FILLED' && latestCapture) {
    return (
      <button
        type="button"
        data-testid={`evidence-slot-${slot.slotId}`}
        onClick={() => onOpenLightbox(latestCapture.documentId)}
        className="flex flex-col text-left gap-2 rounded-lg border border-frozenGrayShades2 dark:border-frozenGrayShades8 p-3 hover:shadow-md transition-shadow cursor-pointer">
        <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 size={16} />
          <span className="text-sm font-medium">{slot.label}</span>
        </div>
        {latestCapture.thumbnailUrl && (
          <img
            src={latestCapture.thumbnailUrl}
            alt={slot.label}
            className="w-full h-28 object-cover rounded-md"
          />
        )}
        <span className="text-xs text-frozenGrayShades6 dark:text-frozenGrayShades4">
          {new Date(latestCapture.capturedAt).toLocaleString()}
          {latestCapture.capturedByName ? ` by ${latestCapture.capturedByName}` : ''}
        </span>
        <SlotStatusBadge variant={slot.required ? 'required' : 'optional'} />
      </button>
    );
  }

  if (slot.status === 'FAILED') {
    return (
      <button
        type="button"
        data-testid={`evidence-slot-${slot.slotId}`}
        onClick={() => latestCapture && onOpenLightbox(latestCapture.documentId)}
        className="flex flex-col text-left gap-2 rounded-lg border border-dashed border-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 cursor-pointer">
        <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
          <AlertTriangle size={16} />
          <span className="text-sm font-medium">{slot.label}</span>
        </div>
        <div className="flex items-center justify-center h-28 rounded-md border border-dashed border-amber-400 text-amber-600 dark:text-amber-400 text-sm font-medium">
          UPLOAD FAILED
        </div>
        <span className="text-xs text-frozenGrayShades6 dark:text-frozenGrayShades4">
          Attempted {latestCapture ? new Date(latestCapture.capturedAt).toLocaleString() : ''} — ask field to retry
        </span>
        <SlotStatusBadge variant={slot.required ? 'required' : 'optional'} />
      </button>
    );
  }

  // EMPTY — always a rendered, bordered, labeled cell, per the sprint's business driver.
  return (
    <div
      data-testid={`evidence-slot-${slot.slotId}`}
      className="flex flex-col gap-2 rounded-lg border border-dashed border-frozenGrayShades4 dark:border-frozenGrayShades7 p-3">
      <div
        className={`flex items-center gap-1 ${
          slot.required ? 'text-red-600 dark:text-red-400' : 'text-frozenGrayShades6 dark:text-frozenGrayShades4'
        }`}>
        <CameraOff size={16} />
        <span className="text-sm font-medium">{slot.label}</span>
      </div>
      <div className="flex items-center justify-center h-28 rounded-md border border-dashed border-frozenGrayShades3 dark:border-frozenGrayShades8 text-frozenGrayShades5">
        <CameraOff size={24} />
      </div>
      <span className="text-xs text-frozenGrayShades6 dark:text-frozenGrayShades4">Not yet captured</span>
      <SlotStatusBadge variant={slot.required ? 'missing' : 'optional'} />
    </div>
  );
};

export default EvidenceSlotCard;
